import React, { useState } from 'react';
import { useLanguage } from "@/contexts/LanguageContext";
import { Instagram, Linkedin, Mail, CheckCircle, AlertCircle } from "lucide-react";
import axios from 'axios';

export const Footer = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState(''); // '', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage(language === 'de' ? 'Bitte gültige E-Mail eingeben' : 'Please enter a valid email');
      return;
    }
    if (!name.trim()) {
      setStatus('error');
      setMessage(language === 'de' ? 'Bitte Namen eingeben' : 'Please enter your name');
      return;
    }

    setStatus('loading');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`,
        null,
        { params: { email, name: name.trim() } }
      );
      setStatus('success');
      setMessage(language === 'de' ? 'Erfolgreich angemeldet!' : 'Successfully subscribed!');
      setEmail('');
      setName('');
      setTimeout(() => { setStatus(''); setMessage(''); }, 5000);
    } catch (error) {
      setStatus('error');
      setMessage(language === 'de' ? 'Fehler beim Anmelden' : 'Subscription failed');
    }
  };

  return (
    <footer className="bg-white text-black py-16 px-4 border-t border-concrete-200">
      <div className="container mx-auto">
        {/* Newsletter Section */}
        <div className="bg-concrete-50 p-8 mb-12 border border-concrete-200">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="w-10 h-10 mx-auto mb-4 text-black" />
            <h3 className="text-2xl font-bold mb-2">
              {language === 'de' ? 'Newsletter abonnieren' : 'Subscribe to Newsletter'}
            </h3>
            <p className="text-concrete-600 mb-6">
              {language === 'de' 
                ? 'Erhalte regelmäßig News zu Events, Community-Highlights und mehr aus dem teamWERK.'
                : 'Get regular news about events, community highlights and more from teamWERK.'}
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-3 max-w-md mx-auto">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={language === 'de' ? 'Dein vollständiger Name' : 'Your full name'}
                className="w-full px-4 py-3 border border-concrete-300 focus:border-black outline-none transition-colors"
                disabled={status === 'loading'}
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={language === 'de' ? 'Deine E-Mail-Adresse' : 'Your email address'}
                  className="flex-1 px-4 py-3 border border-concrete-300 focus:border-black outline-none transition-colors"
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-6 py-3 bg-black text-white hover:bg-concrete-800 transition-colors disabled:opacity-50 font-medium"
                >
                  {status === 'loading' 
                    ? (language === 'de' ? 'Wird angemeldet...' : 'Subscribing...')
                    : (language === 'de' ? 'Anmelden' : 'Subscribe')}
                </button>
              </div>
              <p className="text-xs text-concrete-600">
                {language === 'de'
                  ? <>Nach der Anmeldung erhältst du eine E-Mail mit Bestätigungslink (Double-Opt-In). Erst danach bist du eingetragen. Abmelden kannst du dich jederzeit über den Link in jedem Newsletter. Mehr in der <a href="/datenschutz" className="underline hover:text-black">Datenschutzerklärung</a>.</>
                  : <>After signing up you will receive an email with a confirmation link (double opt-in). Only then are you subscribed. You can unsubscribe at any time via the link in every newsletter. More in our <a href="/datenschutz" className="underline hover:text-black">privacy policy</a>.</>}
              </p>
            </form>
            {message && (
              <div className={`mt-4 flex items-center justify-center gap-2 ${
                status === 'success' ? 'text-green-600' : 'text-red-600'
              }`}>
                {status === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                {message}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2">
              <h2 className="text-3xl font-heading font-bold mb-6 tracking-tighter text-black">TEAMWERK</h2>
              <p className="text-concrete-600 max-w-sm mb-8">
                  {t('infos')} | {t('offices')} | {t('community')}
              </p>
              <div className="flex gap-4">
                  <a href="https://www.instagram.com/teamwerk_official/" target="_blank" rel="noopener noreferrer" aria-label="Teamwerk auf Instagram" className="w-10 h-10 border border-concrete-300 flex items-center justify-center hover:bg-black hover:text-white transition-all cursor-pointer text-black rounded-sm">
                      <Instagram size={20} aria-hidden="true" />
                  </a>
                  <a href="https://www.linkedin.com/company/teamwerk-erolzheim/?viewAsMember=true" target="_blank" rel="noopener noreferrer" aria-label="Teamwerk auf LinkedIn" className="w-10 h-10 border border-concrete-300 flex items-center justify-center hover:bg-black hover:text-white transition-all cursor-pointer text-black rounded-sm">
                      <Linkedin size={20} aria-hidden="true" />
                  </a>
              </div>
          </div>
          
          <div>
              <h3 className="text-lg font-bold mb-6 uppercase tracking-wider text-concrete-400">{t('contact')}</h3>
              <ul className="space-y-4 text-concrete-800">
                  <li className="font-bold">Bürokult GmbH</li>
                  <li>Espachstraße 13</li>
                  <li>88453 Erolzheim</li>
                  <li className="pt-4"><a href="tel:+497354946920" className="hover:text-black">07354 / 94692-0</a></li>
                  <li><a href="mailto:info@buerokult.de" className="hover:text-black">info@buerokult.de</a></li>
              </ul>
          </div>

          <div>
              <h3 className="text-lg font-bold mb-6 uppercase tracking-wider text-concrete-400">Links</h3>
              <ul className="space-y-4 text-concrete-800">
                  <li><a href="/impressum" className="hover:text-black transition-colors">{t('imprint')}</a></li>
                  <li><a href="/datenschutz" className="hover:text-black transition-colors">{t('privacy')}</a></li>
                  <li><a href="/agb" className="hover:text-black transition-colors">{t('terms')}</a></li>
              </ul>
          </div>
        </div>
      </div>
      <div className="container mx-auto mt-16 pt-8 border-t border-concrete-100 text-center text-concrete-400 text-sm">
        &copy; {new Date().getFullYear()} Teamwerk Erolzheim. All rights reserved.
      </div>
    </footer>
  );
};
